import React, { useEffect, useMemo, useRef, useState } from "react";
import { ModalOverlay } from "../../../../components/ModalOverlay";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export const UI = {
  label: "block text-xs font-medium text-slate-500 mb-1.5",
  labelRow:
    "flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5",
  input:
    "w-full px-3 py-2.5 text-sm text-slate-800 bg-white border border-slate-200/90 rounded-xl outline-none transition-all placeholder:text-slate-400 focus:border-primary/40 focus:ring-4 focus:ring-primary/10",
  /** Same as input but without w-full — for title + name flex rows */
  inputInline:
    "px-3 py-2.5 text-sm text-slate-800 bg-white border border-slate-200/90 rounded-xl outline-none transition-all placeholder:text-slate-400 focus:border-primary/40 focus:ring-4 focus:ring-primary/10",
  selectTitle: "shrink-0 w-[6.25rem]",
  inputGrow: "flex-1 min-w-0",
  inputDisabled:
    "w-full px-3 py-2.5 text-sm text-slate-500 bg-slate-50/80 border border-slate-100 rounded-xl cursor-not-allowed",
  readOnly:
    "w-full px-3 py-2.5 text-sm text-slate-800 bg-slate-50/60 border border-transparent rounded-xl font-medium",
  btnPrimary:
    "inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary/20 hover:bg-primary/95 active:scale-[0.99] transition-all disabled:shadow-none",
  btnGhost:
    "inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-primary hover:bg-primary/5 transition-colors",
  btnSecondary:
    "inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200/90 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50/80 transition-all",
  panel: "rounded-2xl border border-slate-200/60 bg-white",
  modalOverlay:
    "fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-slate-900/50 backdrop-blur-md",
  modalShell:
    "flex w-full max-w-md max-h-[min(92vh,720px)] flex-col overflow-hidden rounded-3xl bg-[#f8f9fb] shadow-2xl shadow-slate-900/20 ring-1 ring-white/60",
  modalHeader:
    "flex shrink-0 items-center justify-between gap-3 border-b border-slate-200/70 bg-white/90 px-5 py-4 backdrop-blur-sm",
  modalBody: "flex-1 overflow-y-auto px-4 py-4 sm:px-5 space-y-4",
  modalFooter:
    "shrink-0 border-t border-slate-200/70 bg-white/95 px-5 py-4 backdrop-blur-sm",
};

/** Portaled full-screen backdrop — use instead of a raw div with UI.modalOverlay */
export function AppointmentModalOverlay({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }) {
  return (
    <ModalOverlay className={UI.modalOverlay} {...props}>
      {children}
    </ModalOverlay>
  );
}

/** App shell — same visual language as appointment modals */
export const LAYOUT = {
  root: "layout-root-ios h-screen min-h-0 bg-[#f8f9fb] text-slate-800 md:h-screen dark:bg-slate-950 dark:text-slate-100",
  sidebar:
    "fixed top-0 left-0 h-full z-50 bg-white border-r border-slate-200/70 shadow-[0_4px_24px_rgba(15,23,42,0.06)] dark:bg-slate-900 dark:border-slate-800",
  sidebarBrand:
    "flex items-center justify-between border-b border-slate-200/70 bg-white/95 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/95",
  header:
    "h-14 w-full flex shrink-0 items-center justify-between border-b border-slate-200/70 bg-white/90 px-4 shadow-sm backdrop-blur-sm md:px-6 dark:border-slate-800 dark:bg-slate-900/90 pt-[env(safe-area-inset-top)] md:pt-0",
  main:
    "flex-1 min-h-0 overflow-visible bg-[#f8f9fb] dark:bg-slate-950",
  outletScroll:
    "layout-main-ios-scroll overflow-y-auto overflow-x-hidden bg-[#f8f9fb] px-1 pt-1 pb-[calc(0.5rem+env(safe-area-inset-bottom))] md:px-1 md:pb-1 md:pt-1 dark:bg-slate-950",
  mobileOverlay:
    "fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-md md:hidden transition-opacity duration-300",
  navActive:
    "bg-primary text-white shadow-sm shadow-primary/20 [&_.icon-wrapper]:bg-white/20 [&_.icon-wrapper_svg]:text-white",
  navIdle:
    "text-slate-600 hover:bg-primary/5 hover:text-primary dark:text-slate-300 dark:hover:bg-primary/10 dark:hover:text-primary [&_.icon-wrapper]:bg-slate-100 dark:[&_.icon-wrapper]:bg-slate-800",
  btnIcon: `${UI.btnPrimary} !p-0 w-8 h-8 min-w-8 shadow-sm`,
  btnCompact: `${UI.btnPrimary} px-3 py-1.5 text-xs shadow-sm`,
  menuPanel:
    "absolute right-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-2xl border border-slate-200/60 bg-white py-2 shadow-2xl shadow-slate-900/10 dark:border-slate-700 dark:bg-slate-900",
  statusChip:
    "hidden lg:flex items-center gap-3 rounded-2xl border border-slate-200/60 bg-white px-3 py-2 shadow-[0_1px_0_rgba(15,23,42,0.04)] dark:border-slate-700 dark:bg-slate-800",
  iconBtn:
    "rounded-xl p-2 text-slate-600 transition-colors hover:bg-primary/5 hover:text-primary focus:outline-none dark:text-slate-300 dark:hover:bg-primary/10",
  userMenuBtn:
    "flex items-center gap-2 rounded-xl p-1.5 pr-3 transition-colors focus:outline-none hover:bg-primary/5 hover:text-primary dark:hover:bg-primary/10",
};

export function FormSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200/60 bg-white p-4 space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        {hint ? <p className="text-xs text-slate-500 mt-0.5">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

type PurposeOption = { _id: string; purposeName: string };

function normalizePurposeOptions(purposes: any[] | undefined): PurposeOption[] {
  return (purposes ?? [])
    .filter((p) => p && (p._id != null || p.id != null))
    .filter((p) => p.status !== false)
    .map((p) => ({
      _id: String(p._id ?? p.id),
      purposeName: String(p.purposeName ?? "").trim() || "Unnamed purpose",
    }))
    .sort((a, b) =>
      a.purposeName.localeCompare(b.purposeName, undefined, { sensitivity: "base" })
    );
}

export function PurposeSelect({
  purposes,
  value,
  onChange,
  error,
  disabled,
}: {
  purposes: any[] | undefined;
  value: string;
  onChange: (purposeId: string) => void;
  error?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => normalizePurposeOptions(purposes), [purposes]);

  const selected = useMemo(
    () => options.find((p) => p._id === String(value ?? "")),
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((p) => p.purposeName.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(t);
    };
  }, [open]);

  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
          if (open) setQuery("");
        }}
        className={`${UI.input} w-full flex items-center gap-2 text-left ${
          error ? "border-red-400 ring-red-100" : ""
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <AssignmentIcon sx={{ fontSize: 18 }} className="shrink-0 text-slate-400" />
        <span
          className={`min-w-0 flex-1 truncate text-sm ${
            selected ? "text-slate-900 font-medium" : "text-slate-400"
          }`}
        >
          {selected ? selected.purposeName : "Select purpose"}
        </span>
        <KeyboardArrowDownIcon
          sx={{ fontSize: 20 }}
          className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <PurposeDropdown
          searchRef={searchRef}
          query={query}
          setQuery={setQuery}
          filtered={filtered}
          options={options}
          value={value}
          pick={pick}
        />
      ) : null}
    </div>
  );
}

function PurposeDropdown({
  searchRef,
  query,
  setQuery,
  filtered,
  options,
  value,
  pick,
}: {
  searchRef: React.RefObject<HTMLInputElement>;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  filtered: PurposeOption[];
  options: PurposeOption[];
  value: string;
  pick: (id: string) => void;
}) {
  return (
    <div
      className="absolute left-0 right-0 z-[60] mt-1.5 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/5"
      role="listbox"
      aria-label="Visit purposes"
    >
      <div className="border-b border-slate-100 p-2">
        <div className="relative">
          <SearchIcon
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
            sx={{ fontSize: 17 }}
          />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            autoComplete="off"
            className="w-full rounded-lg border border-slate-200 bg-slate-50/80 py-2 pl-8 pr-2 text-sm text-slate-800 outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
          />
        </div>
      </div>

      <PurposeList filtered={filtered} options={options} value={value} pick={pick} />
    </div>
  );
}

function PurposeList({
  filtered,
  options,
  value,
  pick,
}: {
  filtered: PurposeOption[];
  options: PurposeOption[];
  value: string;
  pick: (id: string) => void;
}) {
  return (
    <div className="max-h-48 overflow-y-auto divide-y divide-slate-50">
      {filtered.length === 0 ? (
        <p className="px-3 py-5 text-center text-sm text-slate-500">
          {options.length === 0 ? "No purposes configured" : "No matches"}
        </p>
      ) : (
        filtered.map((p) => {
          const isSelected = String(value) === p._id;
          return (
            <button
              key={p._id}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => pick(p._id)}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                isSelected
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {isSelected ? (
                <CheckIcon sx={{ fontSize: 16 }} className="shrink-0" />
              ) : (
                <span className="w-4 shrink-0" />
              )}
              <span className="truncate">{p.purposeName}</span>
            </button>
          );
        })
      )}
    </div>
  );
}

/** Compact tabs for fast switching (reception desk speed). */
export function PatientTypeSegment({
  value,
  onChange,
}: {
  value: "old" | "new";
  onChange: (v: "old" | "new") => void;
}) {
  const tab = (id: "old" | "new", label: string) => (
    <button
      type="button"
      onClick={() => onChange(id)}
      className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
        value === id
          ? "bg-white text-primary shadow-sm"
          : "text-slate-600 hover:text-slate-900"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-1 p-1 bg-slate-100/90 rounded-xl" role="tablist">
      {tab("old", "Existing")}
      {tab("new", "New patient")}
    </div>
  );
}

const BASE_DURATION_MINUTES = [5, 10, 15, 20, 25, 30];

export function durationChipOptions(currentMinutes?: number | string): number[] {
  const set = new Set(BASE_DURATION_MINUTES);
  const n = parseInt(String(currentMinutes ?? ""), 10);
  if (Number.isFinite(n) && n > 0) set.add(n);
  return Array.from(set).sort((a, b) => a - b);
}

export function DurationChips({
  value,
  options,
  onChange,
}: {
  value: string | number;
  options?: number[];
  onChange: (minutes: number) => void;
}) {
  const selected = parseInt(String(value), 10);
  const chipOptions = useMemo(
    () => options ?? durationChipOptions(value),
    [options, value]
  );
  return (
    <div className="space-y-1.5">
      <DurationChipsRow selected={selected} chipOptions={chipOptions} onChange={onChange} />
      {Number.isFinite(selected) && selected > 0 ? (
        <p className="text-xs text-slate-500 tabular-nums">
          Slot length: <span className="font-medium text-slate-700">{selected} min</span>
        </p>
      ) : null}
    </div>
  );
}

function DurationChipsRow({
  selected,
  chipOptions,
  onChange,
}: {
  selected: number;
  chipOptions: number[];
  onChange: (minutes: number) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Duration">
      {chipOptions.map((mins) => {
        const active = selected === mins;
        return (
          <button
            key={mins}
            type="button"
            onClick={() => onChange(mins)}
            className={`min-w-[3rem] rounded-lg px-2.5 py-1.5 text-sm font-semibold tabular-nums transition-all ${
              active
                ? "bg-primary text-white shadow-sm shadow-primary/25"
                : "bg-white border border-slate-200/90 text-slate-700 hover:border-primary/30 hover:bg-primary/5"
            }`}
          >
            {mins}m
          </button>
        );
      })}
    </div>
  );
}

export function PurposeNativeSelect({
  purposes,
  value,
  onChange,
  error,
  autoFocus,
}: {
  purposes: any[] | undefined;
  value: string;
  onChange: (purposeId: string) => void;
  error?: boolean;
  autoFocus?: boolean;
}) {
  const options = useMemo(() => normalizePurposeOptions(purposes), [purposes]);
  return (
    <select
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${UI.input} ${error ? "border-red-400 ring-red-100" : ""}`}
    >
      <option value="">Purpose — select</option>
      {options.map((p) => (
        <option key={p._id} value={p._id}>
          {p.purposeName}
        </option>
      ))}
    </select>
  );
}

export function PurposeMultiSelect({
  purposes,
  value,
  onChange,
  error,
}: {
  purposes: any[] | undefined;
  value: string[];
  onChange: (purposeIds: string[]) => void;
  error?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => normalizePurposeOptions(purposes), [purposes]);
  const selectedIds = useMemo(
    () => new Set((value ?? []).map((id) => String(id))),
    [value]
  );

  const selectedLabels = useMemo(
    () =>
      options
        .filter((p) => selectedIds.has(p._id))
        .map((p) => p.purposeName),
    [options, selectedIds]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((p) => p.purposeName.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(t);
    };
  }, [open]);

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const remove = (id: string) => {
    onChange((value ?? []).filter((item) => String(item) !== id));
  };

  return (
    <div ref={rootRef} className="relative space-y-2">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
          if (open) setQuery("");
        }}
        className={`${UI.input} w-full flex items-center gap-2 text-left ${
          error ? "border-red-400 ring-red-100" : ""
        } cursor-pointer`}
      >
        <AssignmentIcon sx={{ fontSize: 18 }} className="shrink-0 text-slate-400" />
        <span
          className={`min-w-0 flex-1 truncate text-sm ${
            selectedLabels.length ? "text-slate-900 font-medium" : "text-slate-400"
          }`}
        >
          {selectedLabels.length
            ? selectedLabels.join(", ")
            : "Select purpose(s)"}
        </span>
        <KeyboardArrowDownIcon
          sx={{ fontSize: 20 }}
          className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {selectedLabels.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {options
            .filter((p) => selectedIds.has(p._id))
            .map((p) => (
              <button
                key={p._id}
                type="button"
                onClick={() => remove(p._id)}
                className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/15"
                title="Remove"
              >
                <span className="truncate max-w-[12rem]">{p.purposeName}</span>
                <span aria-hidden>×</span>
              </button>
            ))}
        </div>
      ) : null}

      {open ? (
        <div
          className="absolute left-0 right-0 z-[60] mt-1.5 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/5"
          role="listbox"
          aria-label="Visit purposes"
          aria-multiselectable="true"
        >
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <SearchIcon
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                sx={{ fontSize: 17 }}
              />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                autoComplete="off"
                className="w-full rounded-lg border border-slate-200 bg-slate-50/80 py-2 pl-8 pr-2 text-sm text-slate-800 outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto divide-y divide-slate-50">
            {filtered.length === 0 ? (
              <p className="px-3 py-5 text-center text-sm text-slate-500">
                {options.length === 0 ? "No purposes configured" : "No matches"}
              </p>
            ) : (
              filtered.map((p) => {
                const isSelected = selectedIds.has(p._id);
                return (
                  <button
                    key={p._id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggle(p._id)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                      isSelected
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {isSelected ? (
                      <CheckIcon sx={{ fontSize: 16 }} className="shrink-0" />
                    ) : (
                      <span className="w-4 shrink-0" />
                    )}
                    <span className="truncate">{p.purposeName}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function formatTimeHm(raw?: string): string {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return s.slice(0, 5);
}

export function timeToMinutesHm(raw?: string): number {
  const hm = formatTimeHm(raw);
  if (!hm) return NaN;
  const [h, min] = hm.split(":").map((x) => parseInt(x, 10));
  if (!Number.isFinite(h) || !Number.isFinite(min)) return NaN;
  return h * 60 + min;
}

export function minutesBetweenTimes(start?: string, end?: string): number {
  const startM = timeToMinutesHm(start);
  const endM = timeToMinutesHm(end);
  if (!Number.isFinite(startM) || !Number.isFinite(endM)) return NaN;
  let diff = endM - startM;
  if (diff <= 0) diff += 24 * 60;
  return diff;
}

export function addMinutesToHm(time: string, minutesToAdd: number): string {
  const startM = timeToMinutesHm(time);
  if (!Number.isFinite(startM)) return formatTimeHm(time);
  const total = startM + minutesToAdd;
  const normalized = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(normalized / 60);
  const min = normalized % 60;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

export function toBackendTime(raw?: string): string {
  const hm = formatTimeHm(raw);
  if (!hm) return String(raw ?? "").trim();
  const s = String(raw ?? "").trim();
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(s)) return s;
  return `${hm}:00`;
}

export function formatSlotDate(raw?: string): string {
  if (!raw) return "";
  const d = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function PatientTypeOption({
  selected,
  icon,
  label,
  description,
  onSelect,
}: {
  selected: boolean;
  icon: React.ReactNode;
  label: string;
  description: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group w-full flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all duration-200 ${
        selected
          ? "ring-4 border-primary/40 ring-primary/15 bg-primary/5"
          : "border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-sm"
      }`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${
          selected ? "bg-primary/15 text-primary" : "bg-slate-100 text-slate-500"
        }`}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 pt-0.5">
        <span className="block text-sm font-semibold text-slate-900">{label}</span>
        <span className="block text-xs leading-relaxed text-slate-500 mt-0.5">
          {description}
        </span>
      </span>
    </button>
  );
}
